"use client"

import { Tabs as TabsPrimitive } from "@base-ui/react/tabs"
import { cn } from "cn"

function Tabs({ className, ...props }: TabsPrimitive.Root.Props) {
  return (
    <TabsPrimitive.Root
      data-slot="tabs"
      className={cn("flex flex-col gap-4", className)}
      {...props}
    />
  )
}

/**
 * A segmented control rather than underlined tabs: at this size an underline is a one-pixel
 * difference between "where you are" and "where you are not", which is not enough to carry the
 * whole page. A filled chip on a recessed track reads as a control at a glance.
 *
 * `overflow-y-hidden` is not decoration. `overflow-x: auto` makes the other axis `auto` too - the
 * spec resolves `visible` to `auto` as soon as one axis is not visible - so anything a pixel taller
 * than the track raises a vertical scrollbar for no reason. The padding keeps the focus ring inside.
 */
function TabsList({ className, ...props }: TabsPrimitive.List.Props) {
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        "relative flex w-fit max-w-full items-center gap-1 overflow-x-auto overflow-y-hidden rounded-xl border border-white/10 bg-[#0f1115] p-1 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className
      )}
      {...props}
    />
  )
}

function TabsTrigger({ className, ...props }: TabsPrimitive.Tab.Props) {
  return (
    <TabsPrimitive.Tab
      data-slot="tabs-trigger"
      className={cn(
        "shrink-0 cursor-pointer rounded-lg px-3.5 py-1.5 text-xs font-semibold tracking-wide whitespace-nowrap text-muted-foreground uppercase transition-colors hover:bg-white/5 hover:text-foreground focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-white/40 data-active:bg-[#2b2f39] data-active:text-foreground data-active:shadow-sm",
        className
      )}
      {...props}
    />
  )
}

/**
 * Panels are unmounted while hidden (Base UI's default, kept deliberately): a chart measures itself
 * when it mounts, and one mounted inside a hidden container comes out with no width at all.
 */
function TabsContent({ className, ...props }: TabsPrimitive.Panel.Props) {
  return (
    <TabsPrimitive.Panel
      data-slot="tabs-content"
      className={cn("space-y-6 focus-visible:outline-none", className)}
      {...props}
    />
  )
}

export { Tabs, TabsList, TabsTrigger, TabsContent }
